import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { queryKeys } from '@/shared/hooks/queries/queryKeys'
import {
  sendChatMessage,
  getChatHistory,
} from '@/features/formatter/apis/yakurekiChat'
import {
  ChatConversation,
  SendChatMessageResponse,
  InputType,
} from '@/shared/types/yakurekiChat'

// ========================================
// useChatHistoryQuery
// ========================================

interface UseChatHistoryQueryReturn {
  chatHistory: ChatConversation | null
  isLoading: boolean
  error: Error | null
  refetch: () => void
}

/**
 * チャット履歴取得クエリ（TanStack Query）
 *
 * 責務:
 * - チャット履歴の取得
 * - キャッシュ管理
 *
 * 使用例:
 * ```tsx
 * const { chatHistory, isLoading } = useChatHistoryQuery('yakureki-id')
 * ```
 */
export const useChatHistoryQuery = (
  yakurekiId: string | undefined
): UseChatHistoryQueryReturn => {
  const query = useQuery({
    queryKey: queryKeys.chat.history(yakurekiId ?? ''),
    queryFn: async (): Promise<ChatConversation> => {
      if (!yakurekiId) {
        throw new Error('yakurekiId is required')
      }
      return await getChatHistory(yakurekiId)
    },
    enabled: !!yakurekiId,
    staleTime: 0, // 常に最新を取得
    retry: (failureCount, error) => {
      // 403, 202 はリトライしない
      const status = (error as { response?: { status?: number } })?.response
        ?.status
      if (status === 403 || status === 202) {
        return false
      }
      return failureCount < 3
    },
  })

  return {
    chatHistory: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error ?? null,
    refetch: () => query.refetch(),
  }
}

// ========================================
// useSendChatMessageMutation
// ========================================

interface SendChatMessageVariables {
  yakurekiId: string
  message?: string
  audioFile?: File | Blob
  inputType: InputType
  allowedSections?: string[]
  attachmentIds?: string[]
}

/**
 * チャットメッセージ送信Mutation（TanStack Query）
 *
 * 責務:
 * - チャットメッセージの送信（テキスト・音声）
 * - 成功時にチャット履歴キャッシュをinvalidate
 *
 * 使用例:
 * ```tsx
 * const sendMessage = useSendChatMessageMutation()
 * await sendMessage.mutateAsync({
 *   yakurekiId: 'xxx',
 *   message: 'SOAPを更新してください',
 *   inputType: 'text',
 * })
 * ```
 */
export const useSendChatMessageMutation = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      yakurekiId,
      message,
      audioFile,
      inputType,
      allowedSections,
      attachmentIds,
    }: SendChatMessageVariables): Promise<SendChatMessageResponse> => {
      return await sendChatMessage(yakurekiId, {
        message,
        audioFile,
        inputType,
        allowedSections,
        attachmentIds,
      })
    },
    onSuccess: (_, variables) => {
      // チャット履歴のキャッシュをinvalidate
      queryClient.invalidateQueries({
        queryKey: queryKeys.chat.history(variables.yakurekiId),
      })
      // 薬歴詳細もinvalidate（SOAP更新のため）
      queryClient.invalidateQueries({
        queryKey: queryKeys.yakureki.detail(variables.yakurekiId),
      })
    },
  })
}
